import { Fragment, useEffect, useState } from 'react';
import {
    Dropdown,
    DropdownButton,
    DropdownItem,
    DropdownList,
    DropdownTrigger,
    DropdownTriggerContext,
    SubNavbar,
} from '@bi-digital/react';
import { ChevronDownIcon } from '@bi-digital/icons';
import type { Programme } from '../../../types';

import styles from './ProgrammeNavigation.module.css';

const getSectionName = (element?: HTMLDivElement) => element?.getAttribute('data-menu-label') || '';

export type ProgrammeNavigationProps = {
    programmeName: Programme['name'];
    /** Right side of the navigation bar, normally used for the Apply Now button */
    children?: React.ReactNode;
};

export const ProgrammeNavigation = ({ programmeName, children }: ProgrammeNavigationProps) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [activeDropdownLink, setActiveDropdownLink] = useState<HTMLDivElement | undefined>(undefined);
    const dropdownLinks = [
        ...document.querySelectorAll<HTMLDivElement>(`[data-section="true"][data-menu-label][id]`),
    ].filter((element) => getSectionName(element) && element.id);

    useEffect(() => {
        const updateActiveIndicator = () => {
            const activeDropdownLink = dropdownLinks.findLast((element) => element.getBoundingClientRect().top < 84);
            setActiveDropdownLink(activeDropdownLink || dropdownLinks[0]);
        };

        updateActiveIndicator();

        window.addEventListener('scroll', updateActiveIndicator);
        return () => {
            window.removeEventListener('scroll', updateActiveIndicator);
        };
    }, [dropdownLinks]);

    return (
        <SubNavbar className={styles.programmePageNavigation} data-color-scheme='dark'>
            <div>
                <DropdownTriggerContext open={dropdownOpen} onOpenChange={setDropdownOpen}>
                    <DropdownTrigger
                        className={styles.trigger}
                        endIcon={<ChevronDownIcon aria-hidden='true' className={styles.triggerIcon} />}
                    >
                        <span>
                            <span>{programmeName}</span>
                            <span>{getSectionName(activeDropdownLink) || ''}</span>
                        </span>
                    </DropdownTrigger>
                    <Dropdown placement='bottom-start' padding={0} className={styles.dropdown}>
                        <DropdownList>
                            {dropdownLinks.map((element, index) => (
                                <Fragment key={element.id}>
                                    {index !== 0 && (
                                        <DropdownItem aria-hidden='true'>
                                            <hr />
                                        </DropdownItem>
                                    )}
                                    <DropdownItem>
                                        <DropdownButton
                                            asChild
                                            className={styles.dropdownButton}
                                            onClick={() => setDropdownOpen(false)}
                                            aria-current={activeDropdownLink?.id === element.id}
                                        >
                                            <a href={`#${element.id}`}>{getSectionName(element)}</a>
                                        </DropdownButton>
                                    </DropdownItem>
                                </Fragment>
                            ))}
                        </DropdownList>
                    </Dropdown>
                </DropdownTriggerContext>
                {children}
            </div>
        </SubNavbar>
    );
};
